import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { useCalculatorStore } from '@/store/calculatorStore'
import client from '@/utils/api-client'
import {
  Copy as IconCopy,
  Link as IconLink,
  Send as IconSend,
  Check as IconCheck,
} from 'lucide-react'
import { useState } from 'react'

const ShareCalculations = () => {
  const [sendStatus, setSendStatus] = useState<'idle' | 'success'>('idle')
  const [copyStatus, setCopyStatus] = useState<'idle' | 'success'>('idle')
  const setEmail = useCalculatorStore((state) => state.setEmail)
  const email = useCalculatorStore((state) => state.userDetails.email)
  const uuid = useCalculatorStore((state) => state.uuid)
  const url = `https://www.askprivatist.no/karakterkalkulator?uuid=${uuid}`

  function sendEmailIconAnimation() {
    setSendStatus('success')
    setTimeout(() => {
      setSendStatus('idle')
    }, 3000)
  }

  function copyIconAnimation() {
    setCopyStatus('success')
    setTimeout(() => {
      setCopyStatus('idle')
    }, 1500)
  }

  async function handleSendEmail(event: React.FormEvent) {
    event.preventDefault()
    try {
      await client(
        `/anon-api/users/calculator/${uuid}/${import.meta.env.VITE_APP_VERSION}/send-link`,
        {
          data: {
            sendEmail: email,
            sendSms: '',
          },
        }
      ).then(() => sendEmailIconAnimation())
    } catch (error) {
      console.error(error)
    }
  }

  return (
    <Dialog>
      <DialogTrigger asChild>
        <button
          className="flex items-center gap-2 cursor-pointer hover:text-purple-700 group"
          data-testid="share-calculations-trigger"
        >
          <IconLink className="size-5 text-warm-gray-700 group-hover:text-inherit" />
          <span className="text-sm font-semibold">Del utregning</span>
        </button>
      </DialogTrigger>
      <DialogContent
        className="flex flex-col max-w-[90vw] sm:max-w-[425px] max-h-[50vh]"
        data-testid="share-calculations-dialog"
      >
        <DialogHeader className="p-5 pb-3">
          <DialogTitle className="text-left" data-testid="dialog-title">
            Del utregningen
          </DialogTitle>
          <DialogDescription></DialogDescription>
        </DialogHeader>
        <div
          className="flex flex-col gap-2 mb-4 px-5"
          data-testid="copy-link-section"
        >
          <span className="text-sm font-bold">Kopier lenke:</span>
          <div className="flex items-center gap-2">
            <span
              className="text-sm text-warm-gray-700 truncate"
              data-testid="share-url"
            >
              {url}
            </span>
            <button
              className="text-sm text-warm-gray-700 hover:text-purple-700 cursor-pointer"
              onClick={() => {
                navigator.clipboard.writeText(url)
                copyIconAnimation()
              }}
              data-testid="copy-url-button"
            >
              {copyStatus === 'success' ? (
                <IconCheck
                  className="size-4 text-green-500"
                  data-testid="copy-success-icon"
                />
              ) : (
                <IconCopy className="size-4" data-testid="copy-icon" />
              )}
            </button>
          </div>
        </div>
        <div
          className="flex flex-col gap-2 px-5 pb-5"
          data-testid="email-section"
        >
          <p className="text-sm font-bold">
            Send utregningen til deg på e-post:
          </p>
          <form
            className="flex items-center gap-2"
            onSubmit={handleSendEmail}
            data-testid="email-form"
          >
            <Input
              id="email"
              type="email"
              autoComplete="email"
              pattern="[a-z0-9._%+\-]+@(?!askundervisning\.no)[a-z0-9.\-]+\.[a-z]{2,}$"
              autoFocus
              placeholder="Skriv inn din e-postadresse"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="email-input"
            />
            <button
              className="text-sm text-warm-gray-700 hover:text-purple-700 cursor-pointer"
              type="submit"
              data-testid="send-email-button"
            >
              {sendStatus === 'success' ? (
                <IconCheck
                  className="size-4 text-green-500"
                  data-testid="send-success-icon"
                />
              ) : (
                <IconSend className="size-4" data-testid="send-icon" />
              )}
            </button>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default ShareCalculations
